/**
 * 我的技能（工具箱）—— 每个用户管理自己的技能。
 * 共享库（后台维护）只读可见；本人私有技能可新建/安装/编辑/删除。
 */
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { toast, confirm } from '../store/toastStore';
import { DOCUMENT_ACCEPT } from '../utils/fileTypes';
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  ClipboardDocumentListIcon,
  FolderOpenIcon,
  SparklesIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface Skill {
  id: number; name: string; description: string; prompt_addition: string;
  category: string; source: string; source_url: string; is_active: boolean;
  editable: boolean; is_shared: boolean;
}

const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  builtin: { label: '内置', color: 'bg-primary/10 text-primary' },
  external: { label: '外部', color: 'bg-purple-100 text-purple-700' },
  custom: { label: '自定义', color: 'bg-emerald-100 text-emerald-700' },
};

const emptyForm = { name: '', description: '', prompt_addition: '' };

const UserSkills: React.FC = () => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Skill | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [showInstall, setShowInstall] = useState(false);
  const [installUrl, setInstallUrl] = useState('');
  const [installJson, setInstallJson] = useState('');
  const [installing, setInstalling] = useState(false);
  // 训练
  const [showTrain, setShowTrain] = useState(false);
  const [trainTab, setTrainTab] = useState<'file' | 'text' | 'project'>('file');
  const [trainFile, setTrainFile] = useState<File | null>(null);
  const [trainText, setTrainText] = useState('');
  const [trainProjectId, setTrainProjectId] = useState('');
  const [trainProjects, setTrainProjects] = useState<{ id: number; project_name: string }[]>([]);
  const [trainPreview, setTrainPreview] = useState<any>(null);
  const [training, setTraining] = useState(false);
  const [trainSaving, setTrainSaving] = useState(false);

  const openTrainModal = async () => {
    setShowTrain(true); setTrainPreview(null);
    try {
      const res = await api.get('/api/projects', { params: { page_size: 50 } });
      setTrainProjects((res.data?.items || res.data || []).map((p: any) => ({ id: p.id, project_name: p.project_name || p.name })));
    } catch { setTrainProjects([]); }
  };

  const handleTrain = async () => {
    const hasInput = (trainTab === 'file' && !!trainFile) || (trainTab === 'text' && !!trainText.trim()) || (trainTab === 'project' && !!trainProjectId);
    if (!hasInput) { toast.error('请先选择/输入参考文档'); return; }
    setTraining(true); setTrainPreview(null);
    try {
      const fd = new FormData();
      if (trainTab === 'file' && trainFile) fd.append('file', trainFile);
      if (trainTab === 'text') fd.append('text_data', trainText);
      if (trainTab === 'project') fd.append('project_id', trainProjectId);
      const res = await api.post('/api/user/skills/train', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      setTrainPreview(res.data);
      toast.success('分析完成，请确认后保存');
    } catch (e: any) { toast.error('分析失败: ' + (e?.response?.data?.detail || e?.message || '')); }
    finally { setTraining(false); }
  };

  const handleTrainSave = async () => {
    if (!trainPreview || !trainPreview.prompt_addition) { toast.error('无训练结果'); return; }
    setTrainSaving(true);
    try {
      await api.post('/api/user/skills', {
        name: trainPreview.name || '训练的风格',
        description: trainPreview.description || '',
        prompt_addition: trainPreview.prompt_addition,
        category: 'content',
      });
      toast.success('已保存为我的技能');
      setShowTrain(false); setTrainPreview(null); load();
    } catch (e: any) { toast.error('保存失败: ' + (e?.response?.data?.detail || e?.message || '')); }
    finally { setTrainSaving(false); }
  };

  const load = async () => {
    setLoading(true);
    try { setSkills((await api.get('/api/user/skills')).data || []); }
    catch { toast.error('加载失败'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm(emptyForm); setEditing(null); setShowCreate(false); };

  const handleSave = async () => {
    if (!form.name.trim() || !form.prompt_addition.trim()) { toast.error('名称和提示词不能为空'); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/api/user/skills/${editing.id}`, form); toast.success('已更新'); }
      else { await api.post('/api/user/skills', { ...form, category: 'content' }); toast.success('已创建'); }
      resetForm(); load();
    } catch (e: any) { toast.error(e?.response?.data?.detail || '保存失败'); }
    finally { setSaving(false); }
  };

  const handleInstall = async () => {
    if (!installUrl.trim() && !installJson.trim()) { toast.error('请输入 URL 或粘贴 JSON'); return; }
    setInstalling(true);
    try {
      await api.post('/api/user/skills/install', { url: installUrl, json_content: installJson, category: 'content' });
      toast.success('安装成功');
      setShowInstall(false); setInstallUrl(''); setInstallJson(''); load();
    } catch (e: any) { toast.error(e?.response?.data?.detail || '安装失败'); }
    finally { setInstalling(false); }
  };

  const handleToggle = async (s: Skill) => {
    try { await api.put(`/api/user/skills/${s.id}`, { is_active: !s.is_active }); load(); }
    catch { toast.error('操作失败'); }
  };

  const handleEdit = (s: Skill) => {
    if (!s.editable) { toast.error('共享/内置技能只读，由后台管理'); return; }
    setEditing(s); setShowCreate(false);
    setForm({ name: s.name, description: s.description, prompt_addition: s.prompt_addition });
  };

  const handleDelete = async (s: Skill) => {
    if (!s.editable) { toast.error('共享/内置技能不可删除'); return; }
    if (!await confirm({ title: '确认删除', message: `确定删除技能「${s.name}」吗？`, confirmLabel: '删除', danger: true })) return;
    try { await api.delete(`/api/user/skills/${s.id}`); toast.success('已删除'); load(); }
    catch { toast.error('删除失败'); }
  };

  const shared = skills.filter(s => s.is_shared);
  const mine = skills.filter(s => !s.is_shared);

  if (loading) return <div className="text-center py-12 text-muted-foreground">加载中...</div>;

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-foreground mb-2">我的技能</h1>
      <p className="text-xs text-muted-foreground mb-6">管理你自己的技能；内置/共享技能只读，可直接在写作时选用。</p>

      <div className="flex gap-3 mb-6">
        <button onClick={() => { resetForm(); setShowInstall(false); setShowCreate(true); }}
          className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/80">新建技能</button>
        <button onClick={() => { setShowCreate(false); setEditing(null); setShowInstall(true); }}
          className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700">安装外部技能</button>
        <button onClick={openTrainModal}
          className="px-4 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700">训练技能</button>
      </div>

      {/* 训练技能弹窗 */}
      {showTrain && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowTrain(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                  <SparklesIcon className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900">训练技能</h3>
                  <p className="text-[11px] text-slate-400">上传参考文档，AI 分析写作风格并生成提示词</p>
                </div>
              </div>
              <button onClick={() => setShowTrain(false)} className="p-1.5 hover:bg-slate-100 rounded-md transition-colors">
                <XMarkIcon className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              <div className="grid grid-cols-3 gap-2 mb-5">
                {(['file', 'text', 'project'] as const).map(t => {
                  const icons = { file: ArrowUpTrayIcon, text: ClipboardDocumentListIcon, project: FolderOpenIcon };
                  const labels = { file: '上传文件', text: '粘贴文本', project: '从项目选' };
                  const Icon = icons[t];
                  const active = trainTab === t;
                  return (
                    <button key={t} onClick={() => setTrainTab(t)}
                      className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-[13px] font-medium border transition-colors ${
                        active
                          ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                          : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
                      }`}>
                      <Icon className="w-4 h-4" />
                      {labels[t]}
                    </button>
                  );
                })}
              </div>

              {trainTab === 'file' && (
                <label className="block">
                  <div className={`flex flex-col items-center justify-center py-8 rounded-lg border-2 border-dashed transition-colors cursor-pointer ${
                    trainFile ? 'border-indigo-300 bg-indigo-50/50' : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
                  }`}>
                    <ArrowUpTrayIcon className={`w-8 h-8 mb-2 ${trainFile ? 'text-indigo-500' : 'text-slate-300'}`} />
                    {trainFile ? (
                      <span className="text-sm text-indigo-700 font-medium">{trainFile.name}</span>
                    ) : (
                      <span className="text-sm text-slate-400">点击选择文件，支持 PDF / DOCX / TXT</span>
                    )}
                  </div>
                  <input type="file" accept={DOCUMENT_ACCEPT} onChange={e => setTrainFile(e.target.files?.[0] || null)} className="hidden" />
                </label>
              )}
              {trainTab === 'text' && (
                <textarea value={trainText} onChange={e => setTrainText(e.target.value)}
                  placeholder="粘贴参考方案/投标文件的文本内容（至少200字）" rows={10}
                  className="w-full border border-slate-200 rounded-lg p-3 text-sm resize-none placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300" />
              )}
              {trainTab === 'project' && (
                <select value={trainProjectId} onChange={e => setTrainProjectId(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg p-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300">
                  <option value="">选择项目...</option>
                  {trainProjects.map(p => (<option key={p.id} value={String(p.id)}>{p.project_name}</option>))}
                </select>
              )}
              <button onClick={handleTrain} disabled={training}
                className="mt-5 w-full py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                {training ? '正在分析…' : '开始分析'}
              </button>

              {trainPreview && (
                <div className="mt-5 pt-5 border-t border-slate-100 space-y-4">
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                    <h4 className="text-sm font-semibold text-slate-800">分析完成，请确认以下内容</h4>
                  </div>
                  <label className="block">
                    <span className="text-xs font-medium text-slate-500 mb-1 block">名称</span>
                    <input value={trainPreview.name || ''} onChange={e => setTrainPreview({ ...trainPreview, name: e.target.value })}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-slate-500 mb-1 block">描述</span>
                    <input value={trainPreview.description || ''} onChange={e => setTrainPreview({ ...trainPreview, description: e.target.value })}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-slate-500 mb-1 block">提示词</span>
                    <textarea value={trainPreview.prompt_addition || ''} onChange={e => setTrainPreview({ ...trainPreview, prompt_addition: e.target.value })}
                      rows={10} className="w-full border border-slate-200 rounded-lg p-3 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300" />
                  </label>
                  <button onClick={handleTrainSave} disabled={trainSaving}
                    className="w-full py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                    {trainSaving ? '正在保存…' : '确认并保存为我的技能'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 新建/编辑 */}
      {(showCreate || editing) && (
        <div className="bg-card rounded-xl border border-border p-6 mb-6">
          <h2 className="text-base font-semibold text-foreground mb-4">{editing ? '编辑技能' : '新建技能'}</h2>
          <div className="space-y-4">
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm" placeholder="名称" />
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm" placeholder="简要描述" />
            <textarea value={form.prompt_addition} onChange={e => setForm(f => ({ ...f, prompt_addition: e.target.value }))}
              rows={6} className="w-full border border-border rounded-lg px-3 py-2 text-sm font-mono"
              placeholder="追加到 AI system prompt 的内容..." />
            <div className="flex gap-3">
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/80 disabled:opacity-50">
                {saving ? '保存中...' : (editing ? '更新' : '创建')}</button>
              <button onClick={resetForm} className="px-4 py-2 text-sm text-foreground hover:text-foreground">取消</button>
            </div>
          </div>
        </div>
      )}

      {/* 安装 */}
      {showInstall && (
        <div className="bg-card rounded-xl border border-purple-200 p-6 mb-6">
          <h2 className="text-base font-semibold text-foreground mb-4">安装外部技能</h2>
          <div className="space-y-4">
            <input value={installUrl} onChange={e => { setInstallUrl(e.target.value); setInstallJson(''); }}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              placeholder="GitHub 仓库 / Raw URL" />
            <div className="text-center text-xs text-muted-foreground">—— 或直接粘贴 JSON ——</div>
            <textarea value={installJson} onChange={e => { setInstallJson(e.target.value); setInstallUrl(''); }}
              rows={8} className="w-full border border-border rounded-lg px-3 py-2 text-sm font-mono"
              placeholder='{"name":"...","prompt_addition":"...","description":"..."}' />
            <div className="flex gap-3">
              <button onClick={handleInstall} disabled={installing}
                className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50">
                {installing ? '安装中...' : '安装'}</button>
              <button onClick={() => setShowInstall(false)} className="px-4 py-2 text-sm text-foreground hover:text-foreground">取消</button>
            </div>
          </div>
        </div>
      )}

      {/* 我的技能 */}
      <div className="mb-6">
        <h2 className="text-sm font-semibold text-muted-foreground mb-3">我的技能</h2>
        <SkillTable skills={mine} onToggle={handleToggle} onEdit={handleEdit} onDelete={handleDelete} emptyText="还没有自己的技能，点上方新建或安装" />
      </div>

      {/* 共享/内置 */}
      {shared.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground mb-3">共享 / 内置技能（只读）</h2>
          <SkillTable skills={shared} onToggle={handleToggle} onEdit={handleEdit} onDelete={handleDelete} emptyText="暂无" />
        </div>
      )}
    </div>
  );
};

const SkillTable: React.FC<{
  skills: Skill[]; emptyText: string;
  onToggle: (s: Skill) => void; onEdit: (s: Skill) => void; onDelete: (s: Skill) => void;
}> = ({ skills, emptyText, onToggle, onEdit, onDelete }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>
          <th className="text-left px-4 py-2.5 font-medium text-slate-600 w-[220px]">名称</th>
          <th className="text-left px-4 py-2.5 font-medium text-slate-600 w-20">来源</th>
          <th className="text-left px-4 py-2.5 font-medium text-slate-600 w-16">状态</th>
          <th className="text-left px-4 py-2.5 font-medium text-slate-600 w-28">操作</th>
        </tr>
      </thead>
      <tbody>
        {skills.map(s => (
          <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
            <td className="px-4 py-2.5">
              <div className="font-medium text-slate-800 truncate max-w-[200px]" title={s.name}>{s.name}</div>
              {s.description && <div className="text-[11px] text-slate-400 truncate max-w-[200px] mt-0.5" title={s.description}>{s.description}</div>}
            </td>
            <td className="px-4 py-2.5">
              <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${SOURCE_LABELS[s.source]?.color || 'bg-slate-100 text-slate-600'}`}>
                {SOURCE_LABELS[s.source]?.label || s.source}
              </span>
            </td>
            <td className="px-4 py-2.5">
              <button onClick={() => onToggle(s)} disabled={!s.editable}
                className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${s.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'} ${!s.editable ? 'opacity-60 cursor-not-allowed' : ''}`}>
                {s.is_active ? '启用' : '禁用'}
              </button>
            </td>
            <td className="px-4 py-2.5">
              {s.editable ? (
                <div className="flex items-center gap-1.5 whitespace-nowrap">
                  <button onClick={() => onEdit(s)} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">编辑</button>
                  <button onClick={() => onDelete(s)} className="text-xs text-rose-500 hover:text-rose-700 font-medium">删除</button>
                </div>
              ) : (
                <span className="text-[11px] text-slate-400">只读</span>
              )}
            </td>
          </tr>
        ))}
        {skills.length === 0 && (
          <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">{emptyText}</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export default UserSkills;
